import { log } from "./commons";
import { getComponent } from "./component-mapping";
import { PZN_BLOCK_NAME } from "../../constants/pzn-constants";
import * as pseudoBlockParser from "./pseudo-block-parser";
import { wrapWithMarker } from "./marker-utils";
import { getFeatureFlag } from "./site-config-utils";
import transformCoreBlock from "./core-block-mappings";
import { DeferredCmsHtml, DeferredScriptWrapper } from "../../components/DeferredCmsHtml";
import { disableInlineScripts } from "./cms-deferred-scripts";
// import LazyHydrate from 'react-lazy-hydration'

const CODEBLOCK_NAMES = ["qbmds/codeblock", "gwp-cg-components/codeblock"];
const WIDGET_NAMES = ["gwp-cg-components/widget"];

const getOriginalCodeblockHtml = (blockName, props) => {
  if (!CODEBLOCK_NAMES.includes(blockName) || !props) return null;
  // qbmds/codeblock uses "innerhtml", gwp-cg-components/codeblock uses "htmlContent"
  return props.innerhtml || props.htmlContent || null;
};

const renderComp = (block, metaData, logTrace, index=0, context = {}) => {
  const { blockName, props } = transformCoreBlock(block);
  const Comp = getComponent(blockName);
  if (!Comp) {
    log.error(
      `${logTrace};EVENT_NAME=EVENT_INNER_BLOCK_PARSER_ERROR No import mapping found for inner block : ${blockName}`
    );
    return;
  }

  const codeblockHtml = getOriginalCodeblockHtml(blockName, props);
  const hasScripts = codeblockHtml && /<script\b/i.test(codeblockHtml);
  const disableDeferredScript = props?.disableDeferredScript;

  if (hasScripts && !disableDeferredScript) {
    log.debug(`${logTrace};EVENT_NAME=EVENT_INNER_BLOCK_PARSER_COMPLETED;DEFERRED=true`);
    const disabledHtml = disableInlineScripts(codeblockHtml);
    const htmlPropKey = props.innerhtml ? "innerhtml" : "htmlContent";
    const deferredProps = { ...props, [htmlPropKey]: disabledHtml };
    return (
      <DeferredScriptWrapper
        key={index}
        html={codeblockHtml}
        logTrace={logTrace}
        blockName={blockName}
      >
        <Comp
          {...deferredProps}
          {...context}
          metaLinkTags={metaData.metaLinkTags}
          ssrContext={{
            locale: metaData.locale,
            env: metaData.env,
            pageUrl: metaData.seo_canonical
          }}
        >
          {parseInnerBlocks(block, metaData, logTrace, context)}
        </Comp>
      </DeferredScriptWrapper>
    );
  }

  const widgetScriptHtml = WIDGET_NAMES.includes(blockName) && props?.priceCatalogMarkup && /<script\b/i.test(props.priceCatalogMarkup) ? props.priceCatalogMarkup : null;

  if (widgetScriptHtml && !disableDeferredScript) {
    log.debug(`${logTrace};EVENT_NAME=EVENT_INNER_BLOCK_PARSER_COMPLETED;DEFERRED=true;BLOCK=widget`);
    const deferredProps = { ...props, priceCatalogMarkup: disableInlineScripts(widgetScriptHtml) };
    return (
      <DeferredScriptWrapper
        key={index}
        html={widgetScriptHtml}
        logTrace={logTrace}
        blockName={blockName}
      >
        <Comp
          {...deferredProps}
          {...context}
          metaLinkTags={metaData.metaLinkTags}
          ssrContext={{
            locale: metaData.locale,
            env: metaData.env,
            pageUrl: metaData.seo_canonical
          }}
        >
          {parseInnerBlocks(block, metaData, logTrace, context)}
        </Comp>
      </DeferredScriptWrapper>
    );
  }

  log.debug(`${logTrace};EVENT_NAME=EVENT_INNER_BLOCK_PARSER_COMPLETED`);
  return (
    <Comp
      {...props}
      {...context}
      key={index}
      metaLinkTags={metaData.metaLinkTags}
      ssrContext={{
        locale: metaData.locale,
        env: metaData.env,
        pageUrl: metaData.seo_canonical
      }}
    >
      {parseInnerBlocks(block, metaData, logTrace, context)}
    </Comp>
  );
};

const parsePseudoBlocks = (block, metaData, logTrace, context = {}) => {
  let { innerBlocks = null, props: { attributes: ancestralAttributes } = {} } =
    block;
  const newAttributes = pseudoBlockParser.makePseudoBlockAttributes(
    block,
    metaData,
    logTrace
  );

  return innerBlocks?.map((childBlock) => {
    const { props, blockName: childBlockName } = childBlock;
    childBlock.props = pseudoBlockParser.enrichPropsWithAttributes(
      props,
      ancestralAttributes,
      newAttributes
    );

    if (pseudoBlockParser.PSEUDO_BLOCK_WHITELIST.includes(childBlockName)) {
      log.debug(
        `${logTrace};EVENT_NAME=EVENT_FOUND_PSEUDO_BLOCK;PSEUDO_BLOCK_NAME=${childBlockName}`
      );

      /* since current block is a pseudo block, we add markers to the dom */
      return wrapWithMarker(
        childBlockName,
        childBlock,
        metaData,
        parsePseudoBlocks(childBlock, metaData, logTrace, context)
      );
    } else {
      return renderComp(childBlock, metaData, logTrace, 0, context);
    }
  });
};

const parseInnerBlocks = (block, metaData, logTrace, context = {}) => {
  let { blockName, innerBlocks = null, props: { children } = {} } = block;
  const { attributes } = block.props;

  if (!innerBlocks) {
    log.debug(`${logTrace};EVENT_NAME=EVENT_INNER_BLOCK_NOT_FOUND`);
    if (!children) return null;
    return /<script\b/i.test(children) ? (
      <DeferredCmsHtml
        html={children}
        logTrace={logTrace}
        blockName={blockName}
      />
    ) : (
      <div dangerouslySetInnerHTML={{ __html: children }} />
    );
  }

  return innerBlocks.map((iBlock, index) => {
    setHasEnableSSR(iBlock, metaData);
    const { blockName: iBlockName, props } = iBlock;
    if (pseudoBlockParser.PSEUDO_BLOCK_WHITELIST.includes(blockName)) {
      iBlock.props = pseudoBlockParser.enrichPropsWithAttributes(
        props,
        attributes
      );
    }
    logTrace += `;INNER_BLOCK_NAME=${iBlockName}`;
    log.debug(`${logTrace};EVENT_NAME=EVENT_INNER_BLOCK_PARSER_INITIATED`);

    if (pseudoBlockParser.PSEUDO_BLOCK_WHITELIST.includes(iBlockName)) {
      log.debug(
        `${logTrace};EVENT_NAME=EVENT_FOUND_PSEUDO_BLOCK;PSEUDO_NAME=${iBlockName}`
      );

      /* since current block is a pseudo block, we add markers to the dom */
      return wrapWithMarker(
        iBlockName,
        iBlock,
        metaData,
        parsePseudoBlocks(iBlock, metaData, logTrace, context)
      );
    } else {
      return renderComp(iBlock, metaData, logTrace, index, context);
    }
    // Drop null children so components that clone every child (e.g. @mds/reusable-blocks) don't crash.
  }).filter((child) => child != null);
};

/**
 * get pzn recommendation for the block
 * @param {string} blockName name of the block
 * @param {object} block block payload
 * @param {object} metaData metadata object
 * @returns {object} pznRecObj
 */
const getPznRecommendationForBlock = (blockName, block, metaData) => {
  let pznRecObj = {};
  if (blockName === PZN_BLOCK_NAME) {
    metaData.pznRecDetailsArr?.forEach((pznObj) => {
      if (pznObj.personalization_placement === block.accessPoint) {
        pznRecObj = pznObj;
      }
    });
  }
  return pznRecObj;
};

/**
 * get ixp recommendation for the block
 * @param {string} blockName name of the block
 * @param {object} block block payload
 * @param {object} metaData metadata object
 * @returns {object} ixpRecObj
 */
const getIxpRecommendationForBlock = (blockName, block, metaData) => {
  if (blockName !== metaData.reusableBlockName) {
    return {};
  }
  return (
    metaData.ixpDetailsArr?.find(
      (ixpObj) =>
        block.props.externalContentIdentifier === ixpObj.original_content_id
    ) ?? {}
  );
};

/**
 * Set hasEnableSSR in metadata.ssrAccumulator during blockParser iteration (in BodyContent).
 * Middleware reads req._ssrAccumulator.hasEnableSSR in res.end() to check if block data props
 * for those components are removed (filterBlockDataProps in nextDataResponseFormatter to sanitize __NEXT_DATA__).
 * @param {Object} block - block
 * @param {Object} metaData - metadata object
 */
const setHasEnableSSR = (block, metaData) => {
  const isNextDataExclusionEnabled = getFeatureFlag("FEATURE_FLAG_NEXTDATA_EXCLUSION_ENABLED");
  const accumulator = metaData?.ssrAccumulator;
  const isAccumulatorObject = typeof accumulator === "object" && accumulator !== null;
  const isEnableSSRTrue = accumulator?.hasEnableSSR === true;
  const propsHasEnableSSR = block?.props?.enableSSR;

  if (isNextDataExclusionEnabled && isAccumulatorObject && !isEnableSSRTrue && propsHasEnableSSR) {
    accumulator.hasEnableSSR = true;
  }
};

/**
 * Return block to React component
 * @param {array of blocks} blocks
 */
export const blockParser = ({
  blocks = [],
  logTrace,
  context,
  metaData = {}
}) => {
  return blocks.map((block, index) => {
    setHasEnableSSR(block, metaData);

    log.info(`${logTrace};original block: ${JSON.stringify(block.blockName)}`);
    const { blockName, props } = transformCoreBlock(block);
    logTrace += `;BLOCK_NAME=${blockName}`;
    log.debug(`${logTrace};EVENT_NAME=EVENT_BLOCK_PARSER_INITIATED`);

    const Comp = getComponent(blockName);
    if (!Comp) {
      log.error(
        `${logTrace};EVENT_NAME=EVENT_BLOCK_PARSER_ERROR No import mapping found for block : ${blockName}`
      );
      return;
    }
    log.debug(`${logTrace};EVENT_NAME=EVENT_BLOCK_PARSER_COMPLETED`);

    //capturing personalization recommendation data and passing as prop
    const pznRecObj = getPznRecommendationForBlock(blockName, block, metaData);
    const ixpRecObj = getIxpRecommendationForBlock(blockName, block, metaData);

    const codeblockHtml = getOriginalCodeblockHtml(blockName, props);
    const hasScripts = codeblockHtml && /<script\b/i.test(codeblockHtml);
    const disableDeferredScript = props?.disableDeferredScript;

    const compProps = {
      ...(hasScripts
        ? { ...props, [props.innerhtml ? "innerhtml" : "htmlContent"]: disableInlineScripts(codeblockHtml) }
        : props),
      ...context,
      metaLinkTags: metaData.metaLinkTags,
      pznRecData: pznRecObj,
      ixpRecData: ixpRecObj,
      ...(blockName === PZN_BLOCK_NAME &&
        pznRecObj?.externalContentIdentifier && {
          externalContentIdentifier: pznRecObj.externalContentIdentifier
        })
    };

    const comp = (
      <Comp
        {...compProps}
        ssrContext={{
          locale: metaData.locale,
          env: metaData.env,
          pageUrl: metaData.seo_canonical
        }}
      >
        {parseInnerBlocks(block, metaData, logTrace, context)}
      </Comp>
    );

    return (
      <div
        key={blockName + "-" + index}
        className={blockName === "helix-common/section" ? blockName : null}
      >
        {wrapWithMarker(
          blockName,
          block,
          metaData,
          hasScripts && !disableDeferredScript ? (
            <DeferredScriptWrapper
              html={codeblockHtml}
              logTrace={logTrace}
              blockName={blockName}
            >
              {comp}
            </DeferredScriptWrapper>
          ) : comp
        )}
      </div>
    );
  });
};
